import httpClient from '@legacy/http/legacy-http-client';
import moment from 'moment';
import { createBrowserHistory } from 'history';
import { STATUS_ACTIVE as TASK_STATUS_ACTIVE, TASK_PRIORITY_NONE } from '@legacy/inc/Task';
import { isObject } from '@client/core/utils/is-object';
import { isNull } from '@client/core/utils/is-null';
import { isUndefined } from '@client/core/utils/is-undefined';
import { laroute } from '@legacy/services/laroute'; // See composer.json "lord/laroute"

/**
 * Generate a URL using a backend route name (i.e. laroute). Similar to backend helper function route().
 *
 * @example
 *  Api.route('users.billing')
 *
 *  window.location = Api.route('users.management.features.edit', {
 *      team_member: 1 // the user id
 *  });
 *
 * @param {String} routeName
 * @param {Object} routeParams
 * @returns {string}
 */
const route = (routeName, routeParams) => {
  const url = laroute.route(routeName, routeParams);

  if (!url) {
    console.error(routeName + ' is not a know route. Maybe need to laroute:generate?');
    return undefined;
  }

  return url;
};

/**
 * Generate a URL to a Laravel route that requires a {project} placeholder.
 * If routeParams.project is not given, the current Project.uuid will be looked
 * up from the current page.
 *
 * @example
 *  projectRoute('analyzer.results', {project: '4b289dd8-30f5-4980-bcb8-a788fa375ebe'});
 *
 * @example
 *  window.location = Api.projectRoute('analyzer.results');
 *
 * @param {String} routeName
 * @param {Object} routeParams
 * @returns {string}
 */
const projectRoute = (routeName, routeParams) => {
  const params = {
    ...routeParams,
  };

  // Many pages load with a "current Project" set in our application config.
  // When projectRoute() is called, if a "project" route param is not given,
  // fallback to using the "current Project" from app config.
  if (typeof params.project === 'undefined' || !params.project) {
    params.project = window.AppConfig.current_project ? window.AppConfig.current_project.uuid : null;
  }

  if (!params.project) {
    console.error('Route "%s" missing value for param "project". Params: %o', routeName, params);
  }

  return route(routeName, params);
};

const browserHistory = createBrowserHistory();
const dbDatetimeFormat = 'YYYY-MM-DD HH:mm:ss';
const dbDateFormat = 'YYYY-MM-DD';

const getApiConfig = () => {
  return {
    headers: {
      Accept: 'application/json',
    },
  };
};

/**
 * Build config for `httpClient.post()` when form data contains an uploaded file.
 *
 * @param formData {FormData}
 * @returns {{}}
 */
const getApiConfigForFileUpload = (formData) => {
  return {
    headers: {
      Accept: 'application/json',
      'Accept-Language': 'en-US,en;q=0.8',
      'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
    },
  };
};

/**
 * Callback for use with AxiosRequestConfig.onUploadProgress. The given object
 * will be updated with a new property "uploadProgress" each time the upload
 * progress is updated. "uploadProgress" is an Integer.
 *
 * For example:
 *
 * // Your Vue component is aware of this object
 * const photo = {id: 123, name: 'Hello', file: File}
 *
 * // Create FormData object with {File}
 * const formData = new FormData()
 * data.append('source', photo.file)
 *
 * const config = getApiConfigForFileUpload(fileData)
 * config.onUploadProgress = Api.onUploadProgress(photo)
 *
 * return httpClient.post(url, data, config).then(r => r.data)
 */
const onUploadProgress = (object) => {
  return (progressEvent) => {
    object.uploadProgress = parseInt(Math.round((progressEvent.loaded / progressEvent.total) * 100));
  };
};

const Api = {
  route: route,
  projectRoute: projectRoute,
  onUploadProgress,
  history: {
    /**
     * Update a search param and replace the current browser history. This is useful for modifying
     * the users current url when UI modifications are being made on the frontend that we want to persist
     * across page loads (i.e. selecting an active item from a list)
     *
     * @param key String,
     * @param value String
     */
    replaceSearchParam: (key, value) => {
      const url = new URL(window.location.href);
      const search_params = url.searchParams;
      search_params.set(key, value);
      url.search = search_params.toString();
      browserHistory.replace(url.toString());
    },
  },
  user: {
    updateProjectKanbanViewMode: (viewMode) => {
      const url = route('api.user.update-project-kanban-view-mode');
      const payload = {
        project_kanban_view_mode: viewMode,
      };
      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },
  },
  projects: {
    create: (workspaceUuid, project) => {
      const url = route('api.project.store', { workspace: workspaceUuid });
      return httpClient.post(url, project, getApiConfig()).then((r) => r.data);
    },
    update: (project, updates) => {
      const url = route('api.projects.update', {
        project: project.uuid,
      });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    getAdders: (project) => {
      const url = route('api.estimator.get-adders', {
        project: project.uuid,
      });

      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
    updateAdders: (project, adders = {}) => {
      const url = route('api.estimator.update-adders', {
        project: project.uuid,
      });

      return httpClient.patch(url, adders, getApiConfig()).then((r) => r.data);
    },
    updatePropertyData: (project) => {
      const url = route('api.projects.update-property-data', {
        project: project.uuid,
      });
      return httpClient.post(url, {}, getApiConfig()).then((r) => r.data);
    },
    addUsers: (project, userIds = []) => {
      const url = route('api.projects.update-team-access', {
        project: project.uuid,
      });
      const payload = {
        userIds,
      };
      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },
  },
  workspaces: {
    inviteUser: (newUser) => {
      return httpClient.post(route('users.invite'), newUser, getApiConfig());
    },
    /**
     * @param id {Number} Member_Account.id
     * @param capability_id {Number} Role enum integer
     */
    updateMember: (id, capability_id) => {
      const payload = { id, capability_id };
      return httpClient.post(route('users.management.update-team-member'), payload, getApiConfig());
    },
    updateInvite: (invite) => {
      const url = route('api.invite.update', {
        inviteId: invite.id,
      });
      return httpClient.patch(url, invite, getApiConfig()).then((r) => r.data);
    },
    deleteInvite: (inviteId) => {
      const url = route('api.invite.destroy', {
        id: inviteId,
      });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
    removeUserAccess: (workspaceUuid, userId) => {
      const url = route('api.workspace.remove-access', { workspace: workspaceUuid, id: userId });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  invites: {
    resend: (id, key) => {
      const url = route('api.invite.resend', { id });
      const payload = { id, key };
      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },
  },
  shareLinks: {
    findOrCreate: (project, endpoint) => {
      const url = `/api/share-links`;
      const data = {
        project_id: project.id,
        endpoint,
      };

      return httpClient.post(url, data).then((response) => response.data.data);
    },
    delete: (project, endpoint) => {
      const url = `/api/share-links/${project.id}/${endpoint}`;

      return httpClient.delete(url).then((r) => r.data);
    },
  },
  rentalAnalyzers: {
    get: (rentalAnalyzer, project) => {
      const url = route('api.rental-analyzers.show', {
        project: project.uuid,
        rental_analyzer: rentalAnalyzer.id,
      });
      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
    update: (rentalAnalyzer, project, updates) => {
      const url = route('api.rental-analyzers.update', {
        project: project.uuid,
        rental_analyzer: rentalAnalyzer.id,
      });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
  },
  // Calls for Expense Accounts. See 'users' property for User Accounts.
  accounts: {
    create: (account) => {
      const url = projectRoute('accounts.store');
      return httpClient.post(url, { name: account.name }).then((r) => r.data);
    },
    update: (account) => {
      const url = projectRoute('accounts.update', { account: account.id });
      return httpClient.patch(url, account).then((r) => r.data);
    },
    delete: (account) => {
      const url = projectRoute('accounts.destroy', { account: account.id });
      return httpClient.delete(url).then((r) => r.data);
    },
  },
  billing: {
    storePaymentMethod: (paymentMethodId) => {
      const payload = { token: paymentMethodId };

      return httpClient.post('/billing/update-payment-information', payload, getApiConfig()).then((r) => r.data);
    },
  },
  ganttTasks: {
    create: (data) => {
      return httpClient.post(projectRoute('api.gantt-tasks.store'), data, getApiConfig()).then((r) => r.data);
    },
    update: (ganttTask, updates, project_uuid) => {
      const url = route('api.gantt-tasks.update', {
        project: project_uuid,
        gantt_task: ganttTask.id,
      });
      return httpClient.patch(url, updates, getApiConfig()).then((response) => response.data);
    },
    delete: (ganttTask) => {
      let data = {
        id: ganttTask.id,
      };

      return httpClient
        .delete(projectRoute('gantt-task.delete', { gantt_task: ganttTask.id }), data)
        .then((r) => r.data);
    },
  },
  ganttLinks: {
    create: (data) => {
      return httpClient.post(projectRoute('gantt-link.store'), data).then((r) => r.data);
    },
    update: (ganttLink) => {
      return httpClient
        .patch(projectRoute('gantt-link.update', { link: ganttLink.id }), ganttLink, getApiConfig())
        .then((r) => r.data);
    },
    delete: (ganttLink) => {
      return httpClient
        .delete(projectRoute('api.gantt-links.destroy', { gantt_link: ganttLink.id }), getApiConfig())
        .then((r) => r.data);
    },
  },
  calendar: {
    /**
     * Get \App\Event in data structure used by Calendar.
     * @param {Number} id Event.id
     */
    getEvent: (id) => {
      const url = route('api.calendar.get-event', { id });
      return httpClient.get(url).then((response) => response.data);
    },
    /**
     * Get \App\Task in data structure used by Calendar.
     * @param {Number} id Task.id
     */
    getTaskManagerTask: (id) => {
      const url = route('api.calendar.get-task', { id });
      return httpClient.get(url).then((response) => response.data);
    },
    /**
     * Get \App\Gantt_Task in data structure used by Calendar.
     * @param {Number} id Gantt_Task.id
     * @deprecated Not used anywhere, can remove
     */
    getGanttTask: async (id) => {
      const url = route('api.calendar.get-gantt-task', { id });
      const response = await httpClient.get(url);

      return response.data;
    },

    /**
     * Update a Gantt_Task from the Calendar.
     *
     * @return Promise<Record<string, any>> Representing a Gantt_Task serialized for the Calendar.
     */
    updateGanttTask: async (projectUuid, taskId, updates) => {
      const url = `/api/project/${projectUuid}/calendar/gantt-task/${taskId}`;
      const response = await httpClient.patch(url, updates);

      return response.data;
    },
  },
  events: {
    create: async (projectUuid, data) => {
      const uri = `/api/project/${projectUuid}/calendar/event`;

      const response = await httpClient.post(uri, data);

      return response.data;
    },
    update: async (projectUuid, eventId, updates) => {
      const uri = `/api/project/${projectUuid}/calendar/event/${eventId}`;

      const response = await httpClient.patch(uri, updates);

      return await response.data;
    },
    delete: async (projectUuid, eventId) => {
      const uri = `/api/project/${projectUuid}/calendar/event/${eventId}`;

      const response = await httpClient.delete(uri);

      return response.data;
    },
  },
  tasks: {
    create: (project, taskList) => {
      let task = {
        name: 'New Item',
        status: TASK_STATUS_ACTIVE,
        priority: TASK_PRIORITY_NONE,
        list_id: taskList.id,
      };

      let tasksStoreUrl = route('tasks.store', {
        project: project.uuid,
      });
      return httpClient.post(tasksStoreUrl, task).then((response) => response.data);
    },

    view: (taskId) => {
      const url = `tasks/${taskId}`;

      return httpClient.get(url).then((response) => response.data.task);
    },

    /**
     * Update a single Task.
     *
     * @param task {Task}
     * @param project {Project}
     */
    update: (task, project) => {
      task.users = task.users || [];

      const data = {
        id: parseInt(task.id),
        name: task.name,
        status: task.status,
        priority: task.priority,
        due_date: task.due_date ? moment(task.due_date).format(dbDateFormat) : null,
        notes: task.notes,

        // Arguments assumes `task.users` is an array of User models.
        // Convert to array of integers expected by backend.
        users: task.users.map((user) => (isObject(user) ? user.id : user)),
      };

      const url = route('tasks.update', {
        project: project.uuid,
        task: data.id,
      });

      return httpClient.patch(url, data).then((response) => response.data);
    },
    delete: (task, project) => {
      const url = route('tasks.destroy', {
        project: project.uuid,
        task: task.id,
      });

      return httpClient.delete(url).then((r) => r.data);
    },
    /**
     * Given Attachment will be associated with the Task.
     *
     * @param projectUuid {String} Project.uuid to associate uploaded file with.
     * @param taskId {Number} Task.id
     * @param attachment {Attachment}
     */
    addAttachment: (projectUuid, taskId, attachment) => {
      const url = route('api.tasks.add-attachment', {
        taskId,
        project: projectUuid,
      });

      const payload = { attachmentId: attachment.id };

      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },

    /**
     * Remove given Attachment from a Task.
     *
     * @param projectUuid {String} Project.uuid to associate uploaded file with.
     * @param taskId {Number} Task.id
     * @param attachmentId {String} Attachment.id
     */
    removeAttachment: (projectUuid, taskId, attachmentId) => {
      const url = route('api.tasks.remove-attachment', {
        project: projectUuid,
        taskId,
        attachmentId,
      });

      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  taskLists: {
    list: (project) => {
      const url = route('api.task-lists.index', {
        project: project.uuid,
      });

      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
    create: (name) => {
      const data = {
        name,
      };

      return httpClient.post(projectRoute('task-lists.store'), data).then((response) => {
        return {
          ...response.data,
          tasks: [],
        };
      });
    },
    update: (taskList, project, updates) => {
      const url = route('task-lists.update', {
        project: project.uuid,
        task_list: taskList.id,
      });
      return httpClient.patch(url, updates).then((r) => r.data);
    },
    delete: (taskList, project) => {
      const url = route('task-lists.destroy', {
        project: project.uuid,
        task_list: taskList.id,
      });

      return httpClient.delete(url).then((r) => r.data);
    },
    reorder: (order) => {
      const data = { order };
      return httpClient.post(projectRoute('api.task-lists.reorder'), data, getApiConfig()).then((r) => r.data);
    },
  },
  taskListTemplates: {
    create: (taskListTemplate, taskList, project) => {
      const url = route('api.task-list-templates.store', {
        project: project.uuid,
      });

      const payload = {
        ...taskListTemplate,
        task_list_id: taskList.id,
      };

      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },
  },
  scopeOfWork: {
    update: (categoryIds) => {
      const url = projectRoute('scope-of-work.update');
      return httpClient.patch(url, { categoryIds }).then((response) => response.data);
    },
  },
  scopeItem: {
    get: (categoryId) => {
      const url = projectRoute('scope-item.show', {
        id: categoryId,
      });
      return httpClient.get(url).then((r) => r.data);
    },
    create: (scopeItem, category, project, display = 1) => {
      const data = {
        body: scopeItem.body,
        category_id: category.id,
        project_id: project.id,
        display: display,
      };
      const url = route('scope-item.store', {
        project: project.uuid,
      });
      return httpClient.post(url, data).then((r) => r.data);
    },
    update: (projectUuid, scopeItemId, updates) => {
      const url = route('api.scope-builder-items.update', {
        project: projectUuid,
        scope_builder_item: scopeItemId,
      });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },

    /**
     * Given Attachment will be displayed as a photo on the given
     * Scope_Builder_Item.
     *
     * @param projectUuid {String} Project.uuid to associate uploaded file with.
     * @param scopeBuilderItemId {Number} Scope_Builder_Item.id
     * @param attachment {Attachment}
     */
    addPhotoFromAttachment: (projectUuid, scopeBuilderItemId, attachment) => {
      const url = route('api.scope-builder-items.photo.add-from-attachment', {
        attachmentId: attachment.id,
        scopeBuilderItemId,
        project: projectUuid,
      });

      return httpClient.post(url, {}, getApiConfig()).then((r) => r.data);
    },

    /**
     * Remove given Attachment from being listed as a photo on the given
     * Scope_Builder_Item.
     *
     * @param projectUuid {String} Project.uuid to associate uploaded file with.
     * @param scopeBuilderItemId {Number} Scope_Builder_Item.id
     * @param attachmentId {String} Attachment.id
     */
    removePhoto: (projectUuid, scopeBuilderItemId, attachmentId) => {
      const url = route('api.scope-builder-items.photo.remove', {
        attachmentId,
        scopeBuilderItemId,
        project: projectUuid,
      });

      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  incomeItems: {
    create: (projectUuid) => {
      const payload = {
        date: moment().format('YYYY-MM-DD'), // TODO: Replace with constant for date format
        name: 'New Item',
      };

      const url = route('api.income-items.store', { project: projectUuid });
      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },
    update: (item, project) => {
      const itemDate = moment(item.date);
      const payload = {
        id: item.id,
        date: itemDate.isValid() ? itemDate.format('YYYY-MM-DD') : null,
        name: item.name,
        type: item.type,
        amount: item.amount,
      };

      const url = route('api.income-items.update', {
        project: project.uuid,
        income_item: item.id,
      });
      return httpClient.patch(url, payload, getApiConfig()).then((r) => r.data);
    },
    delete: (item) => {
      const url = projectRoute('api.income-items.destroy', { income_item: item.id });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  expenseItems: {
    create: (payload, receipt, project) => {
      const date = payload.date ? moment(payload.date).format(dbDatetimeFormat) : null;
      const data = new FormData();
      data.append('date', date);
      // TODO: make sure we can actually delete this information later during update
      if (payload.account_id) data.append('account_id', payload.account_id);
      if (payload.company_id) data.append('company_id', payload.company_id);
      if (payload.class) data.append('class', payload.class);
      if (payload.invoice_no) data.append('invoice_no', payload.invoice_no);
      data.append('line_items', JSON.stringify(payload.line_items));
      data.append('total_tax', payload.total_tax);
      data.append('project_id', project.id);
      if (receipt) data.append('receipt', receipt, receipt.name);

      const url = route('api.expense-item.store', { project: project.uuid });
      return httpClient
        .post(url, data, {
          headers: {
            accept: 'application/json',
            'Accept-Language': 'en-US,en;q=0.8',
            'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
          },
        })
        .then((r) => r.data);
    },
    update: (item, project) => {
      let data = {
        id: item.id,
        name: item.name,
        date: item.date ? moment(item.date).format(dbDatetimeFormat) : null,
        account_id: item.account_id,
        company_id: item.company_id,
        category_id: item.category_id,
        class: item.class,
        amount: item.amount,
        tax: item.tax,
        invoice_no: item.invoice_no,
      };

      const routeParams = { project: project.uuid, expenseItem: data.id };
      const url = route('api.expense-item.update', routeParams);
      return httpClient.patch(url, data, getApiConfig()).then((r) => r.data);
    },
    delete: (item) => {
      const url = projectRoute('api.expense-item.destroy', { expenseItem: item.id });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
    addReceipt: (item, receipt) => {
      const data = new FormData();
      data.append('receipt', receipt, receipt.name);

      const url = projectRoute('api.expense-item.attach-receipt', { expense_item: item.id });
      return httpClient
        .post(url, data, {
          headers: {
            Accept: 'application/json',
            'Accept-Language': 'en-US,en;q=0.8',
            'Content-Type': `multipart/form-data; boundary=${data._boundary}`,
          },
        })
        .then((r) => r.data);
    },
    deleteReceipt: (receipt) => {
      const url = projectRoute('api.expense-item.delete-receipt', { receiptId: receipt.id });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  companies: {
    list: () => {
      const url = route('api.companies.index');
      return httpClient.get(url, {}, getApiConfig()).then((r) => r.data);
    },
    create: (data, files = {}) => {
      const url = route('api.companies.store');
      const formData = companiesPayloadToFormData(data, files);
      return httpClient.post(url, formData, getApiConfigForFileUpload(formData)).then((r) => r.data);
    },
    patch: (company, updates) => {
      const url = route('api.companies.update', { company: company.id });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    update: (company, updates, files = {}) => {
      const formData = companiesPayloadToFormData(updates, files);
      formData.append('_method', 'PATCH'); // Laravel will not process params without this

      const url = route('api.companies.update', { company: company.id });

      return httpClient
        .post(url, formData, {
          headers: {
            Accept: 'application/json',
            'Accept-Language': 'en-US,en;q=0.8',
            'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
          },
        })
        .then((r) => r.data);
    },
    delete: (company) => {
      const url = route('api.companies.update', { company: company.id });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  companyCategories: {
    create: (data) => {
      return httpClient.post('/api/company-categories', data, getApiConfig()).then((r) => r.data);
    },
    update: (companyCategory, updates) => {
      return httpClient
        .patch(`/api/company-categories/${companyCategory.id}`, updates, getApiConfig())
        .then((r) => r.data);
    },
    list: async () => {
      const response = await httpClient.get('/api/company-categories', {}, getApiConfig());

      return response.data;
    },
    delete: (companyCategory) => {
      return httpClient.delete(`/api/company-categories/${companyCategory.id}`, getApiConfig()).then((r) => r.data);
    },
  },
  contacts: {
    create: (data) => {
      const url = route('api.contacts.store');
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    update: (contact, updates) => {
      const url = route('api.contacts.update', { contact: contact.id });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    delete: (contact) => {
      const url = route('api.contacts.destroy', { contact: contact.id });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  contactCategories: {
    create: (data) => {
      const url = route('api.contact-categories.store');
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    update: (contactCategory, updates) => {
      const url = route('api.contact-categories.update', { contact_category: contactCategory.id });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    delete: (contactCategory) => {
      const url = route('api.contact-categories.destroy', { contact_category: contactCategory.id });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  loans: {
    create: (data) => {
      const url = projectRoute('api.loans.store');
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    update: (loan, updates) => {
      const url = projectRoute('api.loans.update', {
        loan: loan.id,
      });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    delete: (loan) => {
      const url = projectRoute('api.loans.destroy', {
        loan: loan.id,
      });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  bridgeLoans: {
    create: (data) => {
      const url = projectRoute('api.bridge-loans.store');
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    update: (loan, updates) => {
      const url = projectRoute('api.bridge-loans.update', {
        bridge_loan: loan.id,
      });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    delete: (loan) => {
      const url = projectRoute('api.bridge-loans.destroy', {
        bridge_loan: loan.id,
      });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  refinanceLoans: {
    /**
     * Create the Project's single Refinance Loan. There can only be one Refinance Loan.
     * @param type {String} "lump_sum" or "pct_arv"
     */
    create: (type) => {
      const url = projectRoute('api.refinance-loans.store');
      const payload = { type };
      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },
    /**
     * Apply partial updates to a RefinanceLoan object.
     * @param loanId {Number} RefinanceLoan.id
     * @param updates {Object} Dictionary of changes
     * @returns {Promise<AxiosResponse<any>>}
     */
    update: (loanId, updates) => {
      const url = projectRoute('api.refinance-loans.update', {
        refinance_loan: loanId,
      });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
  },
  rentalInvestmentPackets: {
    update: (projectUuid, rentalInvestmentPacket) => {
      const url = route('api.rental-investment-packets.update', {
        project: projectUuid,
      });
      return httpClient.put(url, rentalInvestmentPacket, getApiConfig()).then((r) => r.data);
    },

    /**
     * Given Attachment will be displayed as a photo on the given Project's
     * Rental Investment Packet.
     *
     * @param projectUuid {String} Project.uuid to associate uploaded file with.
     * @param attachment {Attachment}
     */
    addPhotoFromAttachment: (projectUuid, attachment) => {
      const url = route('api.rental-investment-packets.photo.add-from-attachment', {
        attachmentId: attachment.id,
        project: projectUuid,
      });

      return httpClient.post(url, {}, getApiConfig()).then((r) => r.data);
    },

    /**
     * Remove given Attachment from being listed as a photo on this given
     * Project's Rental Investment Packet.
     *
     * @param projectUuid {String} Project.uuid to associate uploaded file with.
     * @param attachmentId {String} Attachment.id
     */
    removePhoto: (projectUuid, attachmentId) => {
      const url = route('api.rental-investment-packets.photo.remove', {
        attachmentId: attachmentId,
        project: projectUuid,
      });

      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },

    /**
     * Photos will be displayed in order given on the given Project's
     * Rental Investment Packet.
     *
     * @param projectUuid {String} Project.uuid of Rental Investment Packet
     * @param attachmentIds {Array} Array of Attachment.id in order desired
     */
    reorderPhotos: (projectUuid, attachmentIds) => {
      const url = route('api.rental-investment-packets.reorder-photos', {
        project: projectUuid,
      });

      return httpClient.post(url, { attachmentIds }, getApiConfig()).then((r) => r.data);
    },
  },
  repairCategories: {
    createTopLevelCategory: (projectUuid, data) => {
      const url = route('api.repair-categories.storeTopLevelCategory', { project: projectUuid });
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    createSubcategory: (data) => {
      const url = route('api.repair-categories.storeSubcategory');
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    update: (repairCategory, updates) => {
      const url = route('api.repair-categories.update', { repair_category: repairCategory.id });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    duplicate: (projectUuid, categoryId) => {
      const url = route('api.repair-categories.duplicate', {
        project: projectUuid,
        category: categoryId,
      });

      return httpClient.post(url, {}, getApiConfig()).then((r) => r.data);
    },
    delete: (category) => {
      const url = route('api.repair-categories.destroy', {
        repair_category: category.id,
      });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
    reorder: (order) => {
      const url = route('api.repair-categories.reorder');
      const data = { order };
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    bids: (categoryId) => {
      const url = projectRoute('api.repair-categories.bids', {
        categoryId: categoryId,
      });
      return httpClient.get(url, {}, getApiConfig()).then((r) => r.data);
    },
  },
  repairItems: {
    create: (data) => {
      return httpClient.post(`/api/repair-items`, data, getApiConfig()).then((r) => r.data);
    },
    update: (repairItemId, updates) => {
      const url = route('api.repair-items.update', {
        repair_item: repairItemId,
      });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    delete: (repairItemId) => {
      const url = route('api.repair-items.destroy', { id: repairItemId });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
    reorder: (order) => {
      const url = route('api.repair-items.reorder');
      const data = { order };
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
  },
  templateRepairCategories: {
    // Create a Template_Repair_Category that has no parent, at the top
    // of the category hierarchy
    createFirstLevel: (data) => {
      const url = route('api.template-repair-categories.store_first_category');
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    // Create a Template_Repair_Category that has a parent category anywhere
    // in the category hierarchy
    createSubcategory: (data) => {
      const url = route('api.template-repair-categories.store_subcategory');
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    update: (categoryId, updates) => {
      const url = route('api.template-repair-categories.update', { id: categoryId });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    reorder: (order) => {
      const url = route('api.template-repair-categories.reorder');
      const data = { order };
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    duplicate: (templateId, categoryId) => {
      const url = route('api.template-repair-categories.duplicate', {
        templateId: templateId,
        categoryId: categoryId,
      });

      return httpClient.post(url, {}, getApiConfig()).then((r) => r.data);
    },
    delete: (category) => {
      const url = route('api.template-repair-categories.destroy', {
        categoryId: category.id,
      });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
  },
  repairItemDefaults: {
    create: (payload) => {
      const url = route('api.repair-item-defaults.store');
      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },
    update: (repairItemDefaultId, updates) => {
      const url = route('api.repair-item-defaults.update', { id: repairItemDefaultId });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
    delete: (repairItemDefaultId) => {
      const url = route('api.repair-item-defaults.destroy', { id: repairItemDefaultId });
      return httpClient.delete(url, getApiConfig()).then((r) => r.data);
    },
    reorder: (order) => {
      const url = route('api.repair-item-defaults.reorder');
      return httpClient.post(url, { order }, getApiConfig()).then((r) => r.data);
    },
  },
  repairEstimatorTemplates: {
    createFromProject: (projectUuid, data) => {
      const url = route('api.repair-estimator-templates.create-from-project', { project: projectUuid });
      return httpClient.post(url, data, getApiConfig()).then((r) => r.data);
    },
    get: (repairEstimatorTemplateId) => {
      const url = route('api.repair-estimator-templates.get', { id: repairEstimatorTemplateId });
      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
    getAdders: (templateId) => {
      const url = route('api.repair-estimator-templates.get-adders', { id: templateId });
      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
    updateAdders: (templateId, adders = {}) => {
      const url = route('api.repair-estimator-templates.update-adders', { id: templateId });
      return httpClient.patch(url, adders, getApiConfig()).then((r) => r.data);
    },
    applyToProject: (projectId, templateId) => {
      const url = route('api.repair-estimator-templates.apply-to-project', { project: projectId });
      const payload = { templateId: templateId };

      return httpClient.post(url, payload).then((r) => r.data);
    },
  },
  repairEstimator: {
    editTemplate: (repairEstimatorTemplateId) => {
      const url = route('api.repair-estimator.edit-template', { templateId: repairEstimatorTemplateId });
      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
  },
  estimateReviewChecklists: {
    update: (projectUuid, updates) => {
      const url = route('api.estimate-review-checklists.update', { project: projectUuid });
      return httpClient.patch(url, updates, getApiConfig()).then((r) => r.data);
    },
  },
  repairBids: {
    /**
     * Create a Repair_Bid
     *
     * @param projectUuid {String} Project.uuid
     * @param bid {Object}
     * @param attachment {File|null} Optional. When given, will attach file to created bid.
     * @returns {Promise<AxiosResponse<any>>}
     */
    create: (projectUuid, bid, attachment) => {
      // Using `(bid.something || '')` so null values submitted correctly as strings
      const payload = new FormData();
      payload.append('project_id', bid.project_id);
      payload.append('category_id', bid.category_id);
      payload.append('company_id', bid.company_id || '');
      payload.append('amount', bid.amount || '0');
      payload.append('adjustment', bid.adjustment || '0');
      payload.append('sow_included', bid.sow_included || '');
      payload.append('notes', bid.notes || '');

      if (attachment) {
        payload.append('attachment', attachment, attachment.name);
      }

      const url = route('api.repair-bids.store', {
        project: projectUuid,
      });
      return httpClient
        .post(url, payload, {
          headers: {
            accept: 'application/json',
            'Accept-Language': 'en-US,en;q=0.8',
            'Content-Type': `multipart/form-data; boundary=${payload._boundary}`,
          },
        })
        .then((r) => r.data);
    },

    /**
     * Update a Repair_Bid
     *
     * @param projectUuid {String} Project.uuid
     * @param bid {Object}
     * @param attachment {File|null} Optional. When given, will attach file to created bid.
     * @returns {Promise<AxiosResponse<any>>}
     */
    update: (projectUuid, bid, attachment = null) => {
      const url = route('api.repair-bids.update', {
        project: projectUuid,
        bid: bid.id,
      });

      // Using `(bid.something || '')` so null values submitted correctly as strings
      const payload = new FormData();
      payload.append('project_id', bid.project_id);
      payload.append('category_id', bid.category_id);
      payload.append('company_id', bid.company_id || '');
      payload.append('amount', bid.amount || 0);
      payload.append('adjustment', bid.adjustment || 0);
      payload.append('sow_included', bid.sow_included || '');
      payload.append('notes', bid.notes || '');

      if (attachment) {
        payload.append('attachment', attachment, attachment.name);
      }

      return httpClient
        .post(url, payload, {
          headers: {
            accept: 'application/json',
            'Accept-Language': 'en-US,en;q=0.8',
            'Content-Type': `multipart/form-data; boundary=${payload._boundary}`,
          },
        })
        .then((r) => r.data);
    },

    /**
     * Delete a Repair_Bid
     *
     * @param projectUuid {String} Project.uuid
     * @param bid {Object}
     */
    delete: (projectUuid, bid) => {
      const url = route('api.repair-bids.delete', {
        project: projectUuid,
        bid: bid.id,
      });

      return httpClient.delete(url).then((r) => r.data);
    },

    /**
     * Delete the Repair_Bid attachment
     *
     * @param projectUuid {String} Project.uuid
     * @param bid {Object} Repair_Bid
     * @returns {Promise<AxiosResponse<any>>}
     */
    attachmentDelete: (projectUuid, bid) => {
      const url = route('api.repair-bids.attachment.destroy', {
        project: projectUuid,
        bid: bid.id,
      });

      return httpClient.delete(url).then((r) => r.data);
    },
  },
  schedulerTemplates: {
    create: (data) => {
      return httpClient.post(projectRoute('scheduler-templates.store'), data).then((r) => r.data);
    },
    update: (id, updates) => {
      const url = projectRoute('scheduler-templates.update', { id });
      return httpClient.patch(url, updates).then((r) => r.data);
    },
    delete: (id) => {
      const url = projectRoute('scheduler-templates.delete', { id });
      return httpClient.delete(url).then((r) => r.data);
    },
    /**
     * @param template_id {Number} Scheduler_Template.id
     * @param start_date {String} YYYY-MM-DD date
     * @param projectUuid {String} Project.uuid
     */
    applyToProject: (template_id, start_date, projectUuid) => {
      const payload = {
        template_id,
        start_date,
      };
      const url = route('scheduler-templates.apply-template', { project: projectUuid });

      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },
  },
  attachments: {
    /**
     * Paginated list of all Attachments for the currently active Project.
     */
    index: function () {
      const url = projectRoute('api.attachment.index');
      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
    /**
     * List Attachments across all categories that are detected as photos.
     */
    photos: function () {
      const url = projectRoute('api.attachment.photos');
      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
    /**
     * Create a new Attachment file stored in Project Documents for a Project.
     *
     * @param file {File} Look up from form <input type="file">
     * @param userReadableLabel {String} Label visible to user in Project Documents
     * @param attachmentCategoryId {Integer} Attachment_Category.id
     * @param projectUuid {String} Project.uuid to associate uploaded file with.
     */
    create: (file, userReadableLabel, attachmentCategoryId, projectUuid) => {
      const url = route('attachment.store', { project: projectUuid });

      const formData = new FormData();
      formData.append('source', file, file.name);
      formData.append('name', userReadableLabel);
      formData.append('category_id', attachmentCategoryId);

      return httpClient
        .post(url, formData, {
          headers: {
            accept: 'application/json',
            'Accept-Language': 'en-US,en;q=0.8',
            'Content-Type': `multipart/form-data; boundary=${formData._boundary}`,
          },
        })
        .then((r) => r.data.attachment);
    },
    /**
     * Create a new Attachment file stored in the "Photos" folder within
     * a Project's "Project Documents" tool.
     *
     * @param file {File} Look up from form <input type="file">
     * @param userReadableLabel {String} Label visible to user in Project Documents
     * @param projectUuid {String} Project.uuid to associate uploaded file with.
     * @param asPropertyPhoto {Boolean} Whether this photo should become the Project's Property Photo
     * @param onUploadProgress {Function} Callback for use with AxiosRequestConfig.onUploadProgress
     */
    createPhoto: (file, userReadableLabel, projectUuid, asPropertyPhoto = false, onUploadProgress = null) => {
      const url = route('attachment.store.photo', { project: projectUuid });

      const formData = new FormData();
      formData.append('source', file, file.name);
      formData.append('name', userReadableLabel);

      if (asPropertyPhoto) {
        formData.append('set_property_image', 'on');
      }

      const config = getApiConfigForFileUpload(formData);
      if (onUploadProgress) {
        config.onUploadProgress = onUploadProgress;
      }

      return httpClient.post(url, formData, config).then((r) => r.data.attachment);
    },

    /**
     * Update metadata for a single Attachment. Cannot change the file itself.
     *
     * @param projectUuid {String} Project.uuid
     * @param attachment {Object} Attachment model
     * @param setAsPropertyImage {Boolean} Whether to set this Attachment as the Property Image
     * @returns {Promise<AxiosResponse<any>>}
     */
    update: function (projectUuid, attachment, setAsPropertyImage = false) {
      const url = route('api.attachment.update', {
        project: projectUuid,
        attachmentId: attachment.id,
      });

      const payload = {
        ...attachment,
        set_property_image: !!setAsPropertyImage,
      };

      return httpClient.put(url, payload, getApiConfig()).then((r) => r.data);
    },

    /**
     * Basically a "destroy" method. Detach given Attachment.id from given Project.
     * But Attachments can be associated with multiple Projects (legacy
     * issue with duplicating Projects.) If this is the last Project associated
     * with this Attachment, the Attachment will be deleted.
     *
     * @param projectUuid {String} Project.uuid to detach from
     * @param attachmentId {String} Attachment.id to detach
     * @returns {Promise<AxiosResponse<any>>}
     */
    detachFromProject: function (projectUuid, attachmentId) {
      const url = route('api.attachment.detach-from-project', {
        project: projectUuid,
        attachmentId: attachmentId,
      });

      return httpClient.post(url, {}, getApiConfig()).then((r) => r.data);
    },
  },
  projectAttachmentCategories: {
    /**
     * List of all Attachment Categories for the currently active Project.
     */
    index: function () {
      const url = projectRoute('api.project-attachment-category.index');
      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
    /**
     * Create a Attachment_Category
     * @param projectUuid {String} Project.uuid to associate with the Attachment_Category
     * @param category {Object} Attachment_Category fields
     */
    create: function (projectUuid, category) {
      const url = route('api.project-attachment-category.create', { project: projectUuid });
      return httpClient.post(url, category, getApiConfig()).then((r) => r.data);
    },
    /**
     * Update a Attachment_Category
     * @param projectUuid {String} Project.uuid already associated with the Attachment_Category
     * @param category {Object} Attachment_Category fields. Requires "id" property.
     */
    update: function (projectUuid, category) {
      if (!category.id) {
        throw new Error('category.id must have an ID to update it');
      }

      const url = route('api.project-attachment-category.update', {
        project: projectUuid,
        categoryId: category.id,
      });
      return httpClient.patch(url, category, getApiConfig()).then((r) => r.data);
    },
    /**
     * Delete a Attachment_Category and all Attachments associated with it.
     * @param projectUuid {String} Project.uuid already associated with the Attachment_Category
     * @param categoryId {Number} Attachment_Category.id to delete
     */
    destroy: (projectUuid, categoryId) => {
      const url = route('api.project-attachment-category.destroy', {
        project: projectUuid,
        categoryId: categoryId,
      });
      return httpClient.delete(url).then((r) => r.data);
    },
  },
  photoLog: {
    /**
     * List photos for the Photo Log tool, grouped by Photo Date.
     *
     * @param projectUuid {String} Project.uuid whose Photo Log to list
     */
    index: (projectUuid) => {
      const url = route('api.photo-log.index', { project: projectUuid });

      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
    /**
     * List photos in the Photo Log as list of Attachment models.
     *
     * @param projectUuid {String} Project.uuid whose Photo Log to list
     */
    attachments: (projectUuid) => {
      const url = route('api.photo-log.attachments', { project: projectUuid });

      return httpClient.get(url, getApiConfig()).then((r) => r.data);
    },
  },
  photoLogPhoto: {
    /**
     * Create Photo Log Photos from an array of Attachment.id[]. These
     * Attachments come from Project Documents and will be imported as new
     * Photo Log Photos.
     *
     * @param projectUuid {String} Project.uuid to associate uploaded file with.
     * @param attachmentIds {Number[]} Attachment.id[]
     */
    createFromAttachments: (projectUuid, attachmentIds) => {
      const url = route('api.photo-log-photo.create-from-attachments', { project: projectUuid });

      const payload = { attachmentIds };

      return httpClient.post(url, payload, getApiConfig()).then((r) => r.data);
    },

    /**
     * Update data for a Project Photo Log. Cannot change the file itself.
     *
     * @param projectUuid {String} Project.uuid
     * @param photo {Object} Keys are model fields, Values stored as field values
     */
    update: function (projectUuid, photo) {
      const url = route('api.photo-log-photo.update', {
        project: projectUuid,
        photoId: photo.id,
      });

      return httpClient.put(url, photo, getApiConfig()).then((r) => r.data);
    },

    /**
     * Update order of photos within one Photo Date.
     *
     * `order` array index saved as the corresponding Photo's order.
     * `order` array values are PhotoLogPhoto.id.
     *
     * @param projectUuid {String} Project.uuid
     * @param photo_date {String} YYYY-MM-DD format. PhotoLogPhoto.photo_date of all photos whose order being updated.
     * @param order {Array} Ordered PhotoLogPhoto.id like [51, 43, 55, ...]
     */
    reorder: function (projectUuid, photo_date, order) {
      const url = route('api.photo-log-photo.reorder', {
        project: projectUuid,
      });
      const payload = { photo_date, order };

      return httpClient.put(url, payload, getApiConfig()).then((r) => r.data);
    },

    /**
     * Delete a Photo from a Project Photo Log.
     *
     * @param projectUuid {String} Project.uuid
     * @param photoId {String} PhotoLogPhoto.id to delete
     * @returns {Promise<AxiosResponse<any>>}
     */
    delete: function (projectUuid, photoId) {
      const url = route('api.photo-log-photo.delete', {
        project: projectUuid,
        photoId: photoId,
      });

      return httpClient.delete(url, {}, getApiConfig()).then((r) => r.data);
    },
  },
  projectStructuralModifications: {
    create: (data, projectUuid) => {
      let url = route('api.project_structural_modifications.store', {
        project: projectUuid,
      });
      return httpClient.post(url, data, getApiConfig()).then((response) => response.data);
    },
    update: (data, projectUuid) => {
      let url = route('api.project_structural_modifications.update', {
        id: data.id,
        project: projectUuid,
      });
      return httpClient.put(url, data, getApiConfig()).then((response) => response.data);
    },
    destroy: (id, projectUuid) => {
      let url = route('api.project_structural_modifications.destroy', {
        id,
        project: projectUuid,
      });
      return httpClient.delete(url, getApiConfig()).then((response) => response.data);
    },
  },
};

const companiesPayloadToFormData = (payload, files) => {
  const fd = new FormData();
  const { w9_file, workers_comp_file, general_liability_file } = files;

  // Because we're using form data we need to be able to send empty values, but not null or undefined
  // null and undefined becomes strings and submitted incorrectly to the database
  const isDefined = (value) => !isUndefined(value) && !isNull(value);

  // general
  if (isDefined(payload.name)) fd.append('name', payload.name);
  if (isDefined(payload.license_id)) fd.append('license_id', payload.license_id);
  if (isDefined(payload.category_ids) && Array.isArray(payload.category_ids)) {
    if (payload.category_ids.length > 0) {
      payload.category_ids.forEach((value) => fd.append('category_ids[]', String(value)));
    } else {
      // this is an ugly workaround because FormData does not support an empty array.
      // the backend will need to both loosen validation and inspect separately for this value and interpolate it as
      // an empty array. i hate this but it appears to be a necessary evil.
      fd.append('category_ids', 'empty_category_ids');
    }
  }
  if (isDefined(payload.notes)) fd.append('notes', payload.notes);
  if (isDefined(payload.review)) fd.append('review', payload.review);
  if (isDefined(payload.rating)) fd.append('rating', payload.rating);

  // contact
  if (isDefined(payload.email)) fd.append('email', payload.email);
  if (isDefined(payload.phone)) fd.append('phone', payload.phone);

  // address
  if (isDefined(payload.address_1)) fd.append('address_1', payload.address_1);
  if (isDefined(payload.address_2)) fd.append('address_2', payload.address_2);
  if (isDefined(payload.city)) fd.append('city', payload.city);
  if (isDefined(payload.region)) fd.append('region', payload.region);
  if (isDefined(payload.postal_code)) fd.append('postal_code', payload.postal_code);

  // tax info
  if (isDefined(payload.tax_id)) fd.append('tax_id', payload.tax_id);
  if (isDefined(payload['is_1099_eligible'])) fd.append('is_1099_eligible', payload['is_1099_eligible']);
  if (w9_file) {
    fd.append('w9_file', w9_file, w9_file.name);
  }

  // workers comp
  if (isDefined(payload.workers_comp_insurance)) fd.append('workers_comp_insurance', payload.workers_comp_insurance);
  if (isDefined(payload.workers_comp_insurance_expiration)) {
    const workers_comp_exp = payload.workers_comp_insurance_expiration
      ? moment(payload.workers_comp_insurance_expiration).format('YYYY-MM-DD')
      : '';
    fd.append('workers_comp_insurance_expiration', workers_comp_exp);
  }
  if (workers_comp_file) {
    fd.append('workers_comp_file', workers_comp_file, workers_comp_file.name);
  }

  // general liability
  if (isDefined(payload.general_liability_insurance))
    fd.append('general_liability_insurance', payload.general_liability_insurance);
  if (isDefined(payload.general_liability_insurance_expiration)) {
    const general_liability_exp = payload.general_liability_insurance_expiration
      ? moment(payload.general_liability_insurance_expiration).format('YYYY-MM-DD')
      : '';
    fd.append('general_liability_insurance_expiration', general_liability_exp);
  }
  if (general_liability_file) {
    fd.append('general_liability_file', general_liability_file, general_liability_file.name);
  }

  return fd;
};

export default Api;
